// License Document Section component - extracted from ClassificationStep
// Handles license number input and license file upload with status tracking

import React from "react";
import { classNames } from "../../../utils/classificationHelpers";

export interface LicenseDocumentSectionProps {
  licenseNumber?: string;
  licenseFile?: File | null;
  licenseUploadStatus?: 'idle' | 'uploading' | 'success' | 'error';
  licenseUploadError?: string;
  onLicenseNumberChange: (value: string) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileRemove: () => void;
  onRetryUpload: () => void;
}

const LicenseDocumentSection: React.FC<LicenseDocumentSectionProps> = ({
  licenseNumber,
  licenseFile,
  licenseUploadStatus,
  licenseUploadError,
  onLicenseNumberChange,
  onFileChange,
  onFileRemove,
  onRetryUpload,
}) => {
  return (
    <div className="tw-bg-surface-light dark:tw-bg-surface-dark tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Qualification
          </h3>
        </div>
        <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
          License and certification information for product compliance.
        </p>
      </div>

      <div className="tw-grid tw-gap-6">
        {/* License Number */}
        <div className="tw-flex tw-flex-col tw-gap-2">
          <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
            License Number
          </label>
          <input
            type="text"
            value={licenseNumber || ""}
            onChange={(e) => onLicenseNumberChange(e.target.value)}
            placeholder="e.g., LIC-2024-001234"
            className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
          />
          <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
            Enter the official license number if applicable.
          </p>
        </div>

        {/* License Document */}
        <div className="tw-flex tw-flex-col tw-gap-2">
          <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
            License Document
          </label>

          {/* File Upload Button (shown when no file) */}
          {!licenseFile && (
            <div className="tw-relative">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                onChange={onFileChange}
                className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-opacity-0 tw-cursor-pointer"
              />
              <button
                type="button"
                className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-bg-white dark:tw-bg-gray-800 tw-text-gray-700 dark:tw-text-gray-300 hover:tw-bg-gray-50 dark:hover:tw-bg-gray-700 hover:tw-border-primary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
              >
                <span className="material-symbols-outlined tw-text-lg">
                  description
                </span>
                <span className="tw-text-sm tw-font-medium">
                  Choose Document
                </span>
                <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                  PDF, Images
                </span>
              </button>
            </div>
          )}

          {/* File Preview (shown when file is selected) */}
          {licenseFile && (
            <div className={classNames(
              "tw-border tw-rounded-lg tw-p-3",
              licenseUploadStatus === 'error'
                ? "tw-border-red-300 dark:tw-border-red-600 tw-bg-red-50 dark:tw-bg-red-900/20"
                : "tw-border-gray-300 dark:tw-border-gray-600 tw-bg-gray-50 dark:tw-bg-gray-800"
            )}>
              <div className="tw-flex tw-items-center tw-gap-3">
                {/* File Type Icon */}
                <div className="tw-flex-shrink-0">
                  <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-primary/10 dark:tw-bg-primary/20 tw-flex tw-items-center tw-justify-center tw-relative">
                    <span className="material-symbols-outlined tw-text-primary tw-text-lg">
                      {licenseFile.type.includes("pdf")
                        ? "picture_as_pdf"
                        : "image"}
                    </span>

                    {/* Upload Status Badge */}
                    {licenseUploadStatus === 'uploading' && (
                      <div className="tw-absolute -tw-bottom-1 -tw-right-1 tw-w-4 tw-h-4 tw-rounded-full tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                        <div className="tw-animate-spin tw-rounded-full tw-h-2 tw-w-2 tw-border-2 tw-border-white tw-border-t-transparent" />
                      </div>
                    )}
                    {licenseUploadStatus === 'success' && (
                      <div className="tw-absolute -tw-bottom-1 -tw-right-1 tw-w-4 tw-h-4 tw-rounded-full tw-bg-green-500 tw-flex tw-items-center tw-justify-center">
                        <span className="material-symbols-outlined tw-text-white tw-text-[10px]">check</span>
                      </div>
                    )}
                    {licenseUploadStatus === 'error' && (
                      <div className="tw-absolute -tw-bottom-1 -tw-right-1 tw-w-4 tw-h-4 tw-rounded-full tw-bg-red-500 tw-flex tw-items-center tw-justify-center">
                        <span className="material-symbols-outlined tw-text-white tw-text-[10px]">close</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* File Info */}
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white tw-truncate">
                    {licenseFile.name}
                  </p>
                  <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                    <span>
                      {(licenseFile.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <span className="tw-mx-1">•</span>
                    <span>
                      {licenseFile.type.split("/")[1].toUpperCase()}
                    </span>
                    {licenseUploadStatus === 'uploading' && (
                      <>
                        <span className="tw-mx-1">•</span>
                        <span className="tw-text-blue-600 dark:tw-text-blue-400">Uploading...</span>
                      </>
                    )}
                    {licenseUploadStatus === 'success' && (
                      <>
                        <span className="tw-mx-1">•</span>
                        <span className="tw-text-green-600 dark:tw-text-green-400">Uploaded</span>
                      </>
                    )}
                  </p>
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={onFileRemove}
                  className="tw-flex-shrink-0 tw-p-1 tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
                  title="Remove file"
                >
                  <span className="material-symbols-outlined tw-text-lg">
                    close
                  </span>
                </button>
              </div>

              {/* Error Message and Retry Button */}
              {licenseUploadStatus === 'error' && (
                <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-red-200 dark:tw-border-red-700">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <p className="tw-text-xs tw-text-red-600 dark:tw-text-red-400">
                      {licenseUploadError || 'Upload failed'}
                    </p>
                    <button
                      type="button"
                      onClick={onRetryUpload}
                      className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-rounded tw-text-xs tw-font-medium tw-transition-colors"
                    >
                      <span className="material-symbols-outlined tw-text-[14px]">refresh</span>
                      <span>Retry</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
            Upload official license, certificate, or compliance document (PDF,
            JPG, PNG, GIF, WEBP - Max 10MB)
          </p>
        </div>
      </div>
    </div>
  );
};

export default LicenseDocumentSection;
